"use client";

/**
 * Logo beam effect shared by the onboarding employer-profile step and the
 * create-employer research screen: a soft light dot orbits the logo's border,
 * tinted with the logo's dominant color when one can be extracted.
 */

import React from "react";
import { motion } from "framer-motion";
import {
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from "motion/react";

const DEFAULT_BEAM_COLOR = "rgba(59,130,246,0.9)";

/**
 * Extracts an average color from the logo image (ignoring transparent,
 * near-white, and near-black pixels). Falls back to brand blue when the image
 * can't be sampled (e.g. cross-origin without CORS headers).
 */
export function useLogoPrimaryColor(src?: string | null) {
  const [color, setColor] = React.useState(DEFAULT_BEAM_COLOR);
  React.useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = 32;
        c.height = 32;
        const ctx = c.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, 32, 32);
        const d = ctx.getImageData(0, 0, 32, 32).data;
        let rS = 0,
          gS = 0,
          bS = 0,
          n = 0;
        for (let i = 0; i < d.length; i += 4) {
          const [r, g, b, a] = [d[i], d[i + 1], d[i + 2], d[i + 3]];
          if (
            a < 128 ||
            (r > 240 && g > 240 && b > 240) ||
            (r < 15 && g < 15 && b < 15)
          )
            continue;
          rS += r;
          gS += g;
          bS += b;
          n++;
        }
        if (n > 0)
          setColor(
            `rgba(${Math.round(rS / n)},${Math.round(gS / n)},${Math.round(bS / n)},0.9)`,
          );
      } catch {
        /* cross-origin */
      }
    };
    img.src = src;
  }, [src]);
  return color;
}

export function LogoBeamBorder({
  color,
  duration = 4000,
}: {
  color: string;
  duration?: number;
}) {
  const containerRef = React.useRef<HTMLSpanElement>(null);
  const pathRef = React.useRef<SVGRectElement>(null);
  const progress = useMotionValue(0);
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setSize({ width: r.width, height: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useAnimationFrame((time) => {
    const len = pathRef.current?.getTotalLength?.();
    if (!len) return;
    progress.set((time * (len / duration)) % len);
  });

  const x = useTransform(
    progress,
    (v) => pathRef.current?.getPointAtLength?.(v)?.x ?? 0,
  );
  const y = useTransform(
    progress,
    (v) => pathRef.current?.getPointAtLength?.(v)?.y ?? 0,
  );
  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <span
      ref={containerRef}
      aria-hidden="true"
      className="pointer-events-none absolute -inset-[3px] z-[1] rounded-[16px] border-[3px] border-transparent mask-[linear-gradient(transparent,transparent),linear-gradient(#000,#000)] mask-intersect [mask-clip:padding-box,border-box]"
    >
      <span className="absolute inset-0">
        <svg
          aria-hidden
          className="absolute h-full w-full"
          height="100%"
          width="100%"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            ref={pathRef}
            fill="none"
            height={Math.max(size.height, 1)}
            width={Math.max(size.width, 1)}
            rx="16"
            ry="16"
            x="0"
            y="0"
          />
        </svg>
        <motion.span
          className="absolute left-0 top-0 inline-block"
          style={{ transform }}
        >
          <span
            className="block h-24 w-24 rounded-full opacity-90 blur-[2px]"
            style={{
              background: `radial-gradient(circle, ${color} 0%, ${color.replace("0.9)", "0.5)")} 34%, transparent 72%)`,
            }}
          />
        </motion.span>
      </span>
    </span>
  );
}
